import React, { useState, useReducer, useEffect, useLayoutEffect, useMemo } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import viewerReducer from "../reducer/viewer";
import { getSlideInfo, getAnimalInfo, retrieveS3SharePath } from "../../../redux/reducers/animalResearchSlice";
import _ from "lodash";

export const useViewState = (props) => {
  const usedispatch = useDispatch();

  const [studyInfo, setStudyInfo] = useState(null);

  // 使用函数法调用，类似forceUpdate，https://zh-hans.reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
  const [viewerState, dispatchViewerState] = useReducer(viewerReducer, {
    slideId: props.slideid || localStorage.getItem("slideid") || "",
    animalId: props.animalid || localStorage.getItem("animalid") || "",
    animalInfo: null, // 通过graphql返回的结果
  }, (x) => { console.log(x); return x });

  /** 因为animalid有变化的时候，必须重新运行这个，获取新的animalId */
  useEffect(() => {
    afterChangeAnimalId(viewerState.animalId)
  }, [viewerState.animalId]);

  /**
   * 将这个剥离出来，slideid有变化时，修改studyInfo
   * TODO: 此处加timeout 
   * Error: The keyPrefix reduxPersist: is already in use. Multiple clients cannot share the same keyPrefix. Provide a different keyPrefix in the offlineConfig object.
   */
  useEffect(() => {
    setTimeout(() => {
      afterChangeSlideId(viewerState.slideId);
    }, 0);
  }, [viewerState.slideId]);

  useEffect(() => {
    // AWS-266 cache id to localStorage
    if(viewerState.animalId && viewerState.slideId){
      localStorage.setItem("animalid", viewerState.animalId)
      localStorage.setItem("slideid", viewerState.slideId)
    }
  }, [viewerState.animalId, viewerState.slideId]);

  /** 当animalInfo变化之后，slidesLoading为false和获取新的slides */
  useEffect(() => {
    if (!viewerState.animalInfo) {
      console.log("No animalInfo yet.")
      return;
    };
    // 关闭slidesLoading的效果
    props.setIsShowSlidesLoading && props.setIsShowSlidesLoading(false);
    const info = viewerState.animalInfo
    // 修改slides内容
    props.setSlides && props.setSlides(info ? info.Slides || [] : []);
    // TODO: 是否应该检查slides里面有没有当前的slideId？
    if (info.Slides) {
      const slideInSlides = _.find(
        info.Slides,
        (v) => v?.ID === viewerState.slideId,
      )
      if (!slideInSlides) {
        alert(`Cannot find slide id: ${viewerState.slideId} from animal id: ${viewerState.animalId}`)
      }
    }
  }, [viewerState.animalInfo]);

  useLayoutEffect(() => {
    
    // AWS-258 修改scroll
    const info = studyInfo;
    if (!info) return;
    // 找到这个selected的div
    const selectedDiv = document.querySelector(".SlideList div.selected");
    if (selectedDiv) {
      // selectedDiv.scrollIntoView({ 
      //   behavior: 'smooth', block: 'nearest', inline: 'start' })
      const parentDiv = selectedDiv.parentElement
      const newScrollTop = selectedDiv.offsetTop - selectedDiv.getBoundingClientRect().height / 2
      console.log(parentDiv.scrollTop, newScrollTop)
      const oldScrollTop = parentDiv.scrollTop

      /**
       * 这种使用的是requestAnimationFrame，可用
       */
      const working = () => {
        let start = null;
        const timespan = 400
        const step = (timestamp) => {
          // 每个timestamp都会运行
          if(!start) start=timestamp;
          // 超时就结束
          if(timestamp > start + timespan) {
            parentDiv.scrollTop = newScrollTop
            return;
          }
          const totalDiff = newScrollTop - oldScrollTop
          const stepInterval = totalDiff / timespan
          parentDiv.scrollTop = oldScrollTop + (timestamp - start) * stepInterval
          window.requestAnimationFrame(step) 
        }
        window.requestAnimationFrame(step)
      }

      const notWorking1 = () => {
        parentDiv.animate({
          scrollTop: newScrollTop
        }, 400)
      }

      const notWorking2 = () => {
        parentDiv.animate(
          {
            scrollTop: [oldScrollTop, newScrollTop],
          },
          400
        )
      }
      
      working()
      // notWorking1()
      // notWorking2() 
    }
  }, [studyInfo]);

  // 需要初始化哪个dzi的viewer
  const image = useMemo(() => {
    console.log("running image...", studyInfo)
    let info = null
    if(!studyInfo?.Dzi) return info;
    info = {
      url: studyInfo?.Dzi || "",
      id: "aws-viewer",
      PPM: studyInfo?.PPM, // PPM包括x和y
    }
    console.log("image info:", info)
    return info
  }, [studyInfo]);

  // 获取studyInfo之后，重新计算获取metadata
  const metadata = useMemo(() => {
    console.log("running metadata...", studyInfo)
    if(studyInfo){
      return JSON.parse(studyInfo?.Metadata)
    }
    return null
  }, [studyInfo]);

  // metadata有了之后，填充s3State
  useEffect(() => {
    console.log("running useEffect for metadata...", metadata)
    if (metadata) {
      const s3 = metadata.s3
      if (s3) {
        // 发多次graphql，而不是整合成一次
        for (const key of ['thumbnail', 'slide_overview']) {
          const params = {
            bucket: "test-by-beijing2",
            key: s3[key],
          }
          usedispatch(retrieveS3SharePath(params, (info) => {
            // 使用key，相当于前后端的约定
            props.dispatchS3State && props.dispatchS3State({
              type: key,
              value: {
                [key]: info.SignedUrl,
              }
            })
          }))
        }
      }
    }
  }, [metadata]);

  // 修改通过animalId获取的信息
  const afterChangeAnimalId = (animalid) => {
    if(!animalid) return;
    const slidesParams = { id: animalid };
    usedispatch(getAnimalInfo(slidesParams, (info) => {
      dispatchViewerState(
        {
          type: "animalInfoLoaded",
          value: {
            animalInfo: info
          }
        }
      )
    }));
  };

  const afterChangeSlideId = (slideid) => {
    // param for GraphQL
    // 获取该slide属于哪个study animal 用于右侧data栏展示
    if(!slideid) return;
    // console.log("slideid:", slideid)
    const studyParams = { id: slideid };
    // 获取该slide属于哪个study animal 用于右侧data栏展示
    usedispatch(getSlideInfo(studyParams, (info) => {
      // 存储study信息
      setStudyInfo(info);
      if(props?.reductionAnno && props?.setAnnoHistory) {
        let annohistory = props.reductionAnno(info?.Annotation? info?.Annotation: []);
        // 还原标注
        props.setAnnoHistory(annohistory);
      }
    }));
  };
  

  return {viewerState, studyInfo, image, metadata, dispatchViewerState, afterChangeAnimalId, afterChangeSlideId, setStudyInfo};
}